import { useCallback, useState } from 'react';

import { syncStorage } from 'utils/environment/sync-storage';

type SetStateAction<T> = T | ((prev: T) => T);
type SyncStorageHook<T> = [T | undefined, (value: SetStateAction<T>) => void, () => void];

const createSyncStorageHook = <
  TStorageGet extends (key: any) => any,
  TStorageSet extends (key: any, value: any) => void
>(
  storageGet: TStorageGet,
  storageSet: TStorageSet
) => {
  return <T = NonNullable<ReturnType<TStorageGet>>>(
    key: Parameters<TStorageGet>[0],
    defaultValue: T = (null as unknown) as T
  ): SyncStorageHook<T> => {
    const [state, _setState] = useState<T | undefined>(
      () => (storageGet(key) ?? defaultValue) as T | undefined
    );

    const setState = useCallback(
      (value: SetStateAction<T>) => {
        _setState(prev => {
          const next = typeof value === 'function' ? (value as (prev: T) => T)(prev!) : value;
          storageSet(key, next);
          return next;
        });
      },
      [key]
    );

    const clearState = useCallback(() => {
      _setState(defaultValue);
      syncStorage.remove(key);
    }, [key, defaultValue]);

    return [state, setState, clearState];
  };
};

export const useSyncStorageString = createSyncStorageHook(
  syncStorage.getString,
  syncStorage.setString
);

export const useSyncStorageNumber = createSyncStorageHook(
  syncStorage.getNumber,
  syncStorage.setNumber
);

export const useSyncStorageBoolean = createSyncStorageHook(
  syncStorage.getBoolean,
  syncStorage.setBoolean
);

export const useSyncStorageObject = createSyncStorageHook(
  syncStorage.getObject,
  syncStorage.setObject
);
